import { Checkmark } from "@carbon/icons-react";
import {
  Badge,
  CurrencyDisplay,
  LeftRightFlexedContainer,
  ModalSectionTitle,
  SwitchAppButton,
} from "../../../../components";
import {
  chargeBearer,
  paymentLinkStatus,
  transactionStatus,
} from "../../../../enums";
import { HookFormSelect, SubmitButton, TextInput } from "../../../../hook-form";
import { Payments } from "../../../Payments";
import { CustomerInvoices } from "./CustomerInvoices/CustomerInvoices";
import { IRedirectToInvoiceFormViewProps } from "./RedirectToInvoiceForm";
import { RedirectToPaymentIssueToInvoiceViewModeSelector } from "./RedirectToPaymentIssueToInvoiceViewModeSelector";

export const View = ({
  isLoadingFormData,
  isFormEnabled,
  formDisabledReason,
  customer,
  selectedTxOrInvoice,
  setSelectedTxOrInvoice,
  refundAmount,
  refundCurrency,

  errors,
  handleSubmit,
  register,
}: IRedirectToInvoiceFormViewProps) => {
  return (
    <form
      onSubmit={handleSubmit}
      className="accept-as-partial--form bordered border-dashed b-r-5 pl-5 pr-5 pb-5"
    >
      <ModalSectionTitle
        key_id={
          <>
            Redirect a failed payment to a different payment/invoice for the
            same customer.
          </>
        }
        value={""}
      />
      <div className="bg-gray-2 mt-5 mb-5 pl-5 pr-5 pb-5 pt-5 b-r-5">
        <span className="text-danger">
          NOTE: It must be applied to the same customer
        </span>{" "}
        <br /> Select any active transaction/invoice from the table below with a
        non-successful status ie. NOT{" "}
        <Badge text={"successful"} statusColor={"success"} /> or{" "}
        <Badge text={"partial"} statusColor={"teal"} />
      </div>

      <div className="mt-5">
        <LeftRightFlexedContainer
          key_id="Amount To Redirect"
          value={
            <CurrencyDisplay
              amount={refundAmount}
              currencyOrSymbol={refundCurrency}
            />
          }
        />
      </div>

      <RedirectToPaymentIssueToInvoiceViewModeSelector
        targetCustomer={customer}
        selectedTxOrInvoice={selectedTxOrInvoice}
        selectTargetPaymentView={
          !customer ? null : (
            <Payments
              tableName="customerTransactions"
              // hideColumns={["gateway"]}
              // hideQueryStringTabs={["successful", "partial"]}
              defaultPageParams={{ page_size: 5 }}
              filter={{
                customer_id: customer.id,
              }}
              actions={(payment) => {
                const isActiveItem =
                  selectedTxOrInvoice?.tx_ref === payment.tx_ref;
                const isRedirectButtonDisabled =
                  payment.status === transactionStatus.successful ||
                  payment.status === transactionStatus.partial;

                return (
                  <div>
                    <SwitchAppButton
                      onClick={() => setSelectedTxOrInvoice(payment)}
                      disabled={isActiveItem || isRedirectButtonDisabled}
                      size="xs"
                      kind={isActiveItem ? "primary" : "secondary"}
                      title={
                        isActiveItem
                          ? "This payment is already selected"
                          : !isRedirectButtonDisabled
                          ? "Select Payment"
                          : "This payment is already completed. You cannot redirect to this payment"
                      }
                      className="mt-3 mb-3"
                    >
                      {isActiveItem ? (
                        <>
                          <Checkmark size={20} className="mr-5" /> Selected
                        </>
                      ) : (
                        "Select Payment"
                      )}
                    </SwitchAppButton>
                  </div>
                );
              }}
            />
          )
        }
        selectTargetInvoiceView={
          !customer ? null : (
            <CustomerInvoices
              filter={{
                customer_id: customer.id,
              }}
              actions={(invoice) => {
                const isActiveItem =
                  selectedTxOrInvoice?.tx_ref === invoice.tx_ref;
                const isRedirectButtonDisabled = false;
                // invoice.status === paymentLinkStatus.successful ||
                // invoice.status === transactionStatus.partial;

                return (
                  <div>
                    <SwitchAppButton
                      onClick={() => setSelectedTxOrInvoice(invoice)}
                      disabled={isActiveItem || isRedirectButtonDisabled}
                      size="xs"
                      kind={isActiveItem ? "primary" : "secondary"}
                      title={
                        isActiveItem
                          ? "This invoice is already selected"
                          : !isRedirectButtonDisabled
                          ? "Select Invoice"
                          : "This invoice is already completed. You cannot redirect to this invoice"
                      }
                      className="mt-3 mb-3"
                    >
                      {isActiveItem ? (
                        <>
                          <Checkmark size={20} className="mr-5" /> Selected
                        </>
                      ) : (
                        "Select Invoice"
                      )}
                    </SwitchAppButton>
                  </div>
                );
              }}
            />
          )
        }
      />

      <div className="flex-container flex-break--small">
        <div className="flex-box">
          <TextInput
            name="max_partial_payment_count"
            label="Max Partial Payments Count"
            infoIconText={`
                This determines the total number of partial payments the customer will be allowed to make in order to complete the transaction.`}
            infoIconPosition="bottom"
            type="number"
            register={register}
            errors={errors}
          />
        </div>

        <div className="flex-box ml-md-5">
          <HookFormSelect
            name="bearer"
            label="Charge Bearer"
            infoIconText={
              <>
                This determines who bears the app fees.
                <br />
                <strong>NOTE:</strong> that if it is set to customer, and the
                total amount of partial payments do not cover the fees, then the
                transaction may not settle as SUCCESSFUL, but PARTIAL. <br />
                Then the customer may have to pay extra in order to complete the
                transaction.
              </>
            }
            infoIconPosition="bottom"
            // showSkeleton={isLoadingFormData}
            selectItems={Object.keys(chargeBearer).map((bearerEntry) => ({
              text: `${bearerEntry}`,
              value: bearerEntry,
            }))}
            register={register}
            errors={errors}
          />
        </div>
      </div>

      <TextInput
        name="description"
        label="Description (Optional)"
        placeholder="e.g. Used to settle school fees payment for second child"
        infoIconText="This will help you in the future to recall why you performed this action."
        type="text"
        register={register}
        errors={errors}
      />

      {!formDisabledReason || formDisabledReason === "" ? null : (
        <div className="text-danger mt-5">{formDisabledReason}</div>
      )}

      <div className="flex-container submit-button--container">
        <SubmitButton
          // size="md"
          className="mt-5 flex-box"
          showChildrenWithLoadingIcon
          disabled={!isFormEnabled}
          title={isFormEnabled ? "" : formDisabledReason}
        >
          Redirect To Selected
        </SubmitButton>
      </div>
    </form>
  );
};
